/**
 * Shoplift price testing — derived-price recompute.
 *
 * snippets/price.liquid computes several displays FROM the real product price
 * (member %, event sale %, per-SQFT figures, "you save $X" copy). Shoplift's
 * script only swaps the text of elements tagged with data-sl-attribute-p /
 * data-sl-attribute-cap, so those computed displays would go stale for
 * visitors in a price-test variant.
 *
 * This script watches the tagged "anchor" elements. When Shoplift swaps an
 * anchor's text (i.e. the visitor is in a test variant), every element marked
 * with data-rs-sl-derived + the same data-rs-sl-pid inside the same price
 * block is recomputed from the swapped value, mirroring price.liquid's
 * integer-cents math (truncating percent division, per-SQFT cent rounding).
 *
 * When no price test is running the anchors never change, so this script
 * changes nothing — recompute only fires once an anchor's text differs from
 * the text it was server-rendered with.
 *
 * Derived types (data-rs-sl-derived):
 *   pct-off       price − pct%                     (data-rs-sl-pct)
 *   savings-money price × pct%                     (data-rs-sl-pct)
 *   pps-base      price ÷ sqft                     (data-rs-sl-sqft)
 *   pps-from      min price ÷ sqft                 (data-rs-sl-sqft)
 *   pps-compare   compare-at ÷ sqft                (data-rs-sl-sqft, cap anchor)
 *   pps-sale      (price − pct%) ÷ sqft            (data-rs-sl-pct + sqft)
 *   pps-member    (price − pct%) ÷ sqft            (data-rs-sl-pct + sqft)
 *
 * Formats (data-rs-sl-format): "money" = always 2 decimals,
 * "money_no_zeros" = drop trailing zeros (mirrors money_without_trailing_zeros).
 * Parse/format go through Shopify.parseMoney / Shopify.formatMoney so
 * presentment formats (Rs., €, Ft, ₫, …) stay intact. Heuristic fallback
 * below is only used if format-money.js has not loaded.
 */
(function () {
  'use strict';

  if (window.__rsSlDerivedInit) return;
  window.__rsSlDerivedInit = true;

  var ANCHOR_SELECTOR = '[data-sl-attribute-p], [data-sl-attribute-cap]';
  var SCOPE_SELECTOR = '.code-price-flex-wraper, .price-block, .price-block-container';
  var US_SEP = { thousands: ',', decimal: '.' };
  var EU_SEP = { thousands: '.', decimal: ',' };

  /**
   * EUR uses 1.234,56. USD whole-dollar amounts like $1,470 also contain a
   * comma — that is a thousands separator, not a decimal. Only treat comma as
   * decimal when the currency is EUR, both separators are present and comma
   * is last, or a lone comma is followed by 1–2 digits (€72,45).
   */
  function separatorsFor(text) {
    var s = String(text || '');
    if (/€|\bEUR\b/i.test(s)) return EU_SEP;
    var lastComma = s.lastIndexOf(',');
    var lastDot = s.lastIndexOf('.');
    if (lastComma !== -1 && lastDot !== -1) {
      return lastComma > lastDot ? EU_SEP : US_SEP;
    }
    if (lastComma !== -1 && lastDot === -1) {
      var after = s.substring(lastComma + 1).replace(/\D/g, '');
      if (after.length > 0 && after.length <= 2) return EU_SEP;
    }
    return US_SEP;
  }

  /** Parses a presentment price string into Shopify integer subunits. */
  function parseCents(text) {
    if (!text) return null;
    if (window.Shopify && typeof Shopify.parseMoney === 'function') {
      var parsed = Shopify.parseMoney(text, Shopify.money_format);
      if (parsed != null) return parsed;
    }
    var sep = separatorsFor(text);
    var raw = String(text)
      .replace(/[A-Za-z]{1,4}\./g, '')
      .replace(/[^0-9.,]/g, '');
    if (!raw) return null;
    if (sep.decimal === ',') {
      raw = raw.replace(/\./g, '').replace(',', '.');
    } else {
      raw = raw.replace(/,/g, '');
    }
    var n = parseFloat(raw);
    if (isNaN(n)) return null;
    return Math.round(n * 100);
  }

  /** Extracts the currency symbol from rendered price text (defaults to $). */
  function currencySymbol(text) {
    var m = String(text || '').match(/[^\s0-9.,-]+/);
    return m ? m[0] : '$';
  }

  function noDecimalsMoneyFormat(fmt) {
    if (/amount_no_decimals/.test(fmt)) return fmt;
    return fmt
      .replace('{{amount_with_comma_separator}}', '{{amount_no_decimals_with_comma_separator}}')
      .replace('{{amount_with_space_separator}}', '{{amount_no_decimals_with_space_separator}}')
      .replace('{{amount_with_apostrophe_separator}}', '{{amount_no_decimals_with_apostrophe_separator}}')
      .replace('{{amount}}', '{{amount_no_decimals}}');
  }

  function formatCents(cents, format, symbol, sep) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && Shopify.money_format) {
      var moneyFormat = Shopify.money_format;
      if (format === 'money_no_zeros' && cents % 100 === 0) {
        moneyFormat = noDecimalsMoneyFormat(moneyFormat);
      }
      return String(Shopify.formatMoney(cents, moneyFormat)).replace(/<[^>]*>/g, '');
    }
    sep = sep || US_SEP;
    var dollars = cents / 100;
    var str;
    if (format === 'money_no_zeros' && cents % 100 === 0) {
      str = String(Math.round(dollars));
    } else {
      str = dollars.toFixed(2);
    }
    var parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep.thousands);
    var joined = parts.length > 1 ? parts[0] + sep.decimal + parts[1] : parts[0];
    return symbol + joined;
  }

  /** Mirrors Liquid's `times: pct | divided_by: 100` truncating integer math. */
  function pctAmount(cents, pct) {
    return Math.floor((cents * pct) / 100);
  }

  function anchorPid(el) {
    return el.getAttribute('data-sl-attribute-p') || el.getAttribute('data-sl-attribute-cap');
  }

  /** Finds the p/cap anchors for a derived element, preferring its own price block. */
  function findAnchor(derivedEl, pid, attr) {
    var scope = derivedEl.closest(SCOPE_SELECTOR);
    var sel = '[' + attr + '="' + pid + '"]';
    var anchor = null;
    while (scope) {
      anchor = scope.querySelector(sel);
      if (anchor) return anchor;
      scope = scope.parentElement && scope.parentElement.closest(SCOPE_SELECTOR);
    }
    return document.querySelector(sel);
  }

  function recomputeDerived(derivedEl) {
    var pid = derivedEl.getAttribute('data-rs-sl-pid');
    var type = derivedEl.getAttribute('data-rs-sl-derived');
    if (!pid || !type) return;

    var format = derivedEl.getAttribute('data-rs-sl-format') || 'money';
    var pct = parseFloat(derivedEl.getAttribute('data-rs-sl-pct'));
    var sqft = parseFloat(derivedEl.getAttribute('data-rs-sl-sqft'));

    var anchorAttr = type === 'pps-compare' ? 'data-sl-attribute-cap' : 'data-sl-attribute-p';
    var anchor = findAnchor(derivedEl, pid, anchorAttr);
    if (!anchor) return;

    var anchorText = anchor.textContent;
    var cents = parseCents(anchorText);
    if (cents == null || cents <= 0) return;

    var symbol = currencySymbol(anchorText) || currencySymbol(derivedEl.textContent);
    var sep = separatorsFor(anchorText);
    var out = null;

    switch (type) {
      case 'pct-off':
        if (isNaN(pct)) return;
        out = cents - pctAmount(cents, pct);
        break;
      case 'savings-money':
        if (isNaN(pct)) return;
        out = pctAmount(cents, pct);
        break;
      case 'pps-base':
      case 'pps-from':
      case 'pps-compare':
        if (isNaN(sqft) || sqft <= 0) return;
        out = Math.round(cents / sqft);
        break;
      case 'pps-sale':
      case 'pps-member':
        if (isNaN(pct) || isNaN(sqft) || sqft <= 0) return;
        out = Math.round((cents - pctAmount(cents, pct)) / sqft);
        break;
      default:
        return;
    }

    if (out == null || out < 0) return;
    var next = formatCents(out, format, symbol, sep);
    if (derivedEl.textContent !== next) derivedEl.textContent = next;
  }

  /**
   * Records the server-rendered value so we only act on Shoplift swaps.
   * Liquid-rendered anchors carry data-rs-sl-cents (authoritative, immune to
   * script ordering); JS-rendered anchors (Algolia) fall back to capturing
   * their first-seen text.
   */
  function registerAnchor(el) {
    if (!el.hasAttribute('data-rs-sl-cents') && !el.hasAttribute('data-rs-sl-orig')) {
      el.setAttribute('data-rs-sl-orig', el.textContent);
    }
  }

  function anchorChanged(el) {
    var centsAttr = el.getAttribute('data-rs-sl-cents');
    if (centsAttr !== null) {
      var served = parseInt(centsAttr, 10);
      if (isNaN(served)) return false;
      var current = parseCents(el.textContent);
      return current !== null && current !== served;
    }
    return el.getAttribute('data-rs-sl-orig') !== el.textContent;
  }

  function recomputeForAnchor(anchor) {
    if (!anchorChanged(anchor)) return;
    var pid = anchorPid(anchor);
    if (!pid) return;
    var scope = anchor.closest(SCOPE_SELECTOR) || document;
    var derived = scope.querySelectorAll('[data-rs-sl-derived][data-rs-sl-pid="' + pid + '"]');
    if (!derived.length && scope !== document) {
      derived = document.querySelectorAll('[data-rs-sl-derived][data-rs-sl-pid="' + pid + '"]');
    }
    derived.forEach(recomputeDerived);
  }

  function scanAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(ANCHOR_SELECTOR).forEach(function (el) {
      registerAnchor(el);
      recomputeForAnchor(el);
    });
  }

  /**
   * The sticky ATC bar and the "Add to cart - $X" button label are painted by
   * variant-picker.js from #pdp-price-container BEFORE Shoplift's observer
   * swaps the container's text. Re-run the picker's own sync methods after a
   * swap so both re-read the (now swapped) effective price.
   */
  function resyncPdpChrome() {
    document.querySelectorAll('variant-picker').forEach(function (vp) {
      try {
        if (typeof vp.updateAddToCartButton === 'function') vp.updateAddToCartButton();
        if (typeof vp.syncStickyAtcPrice === 'function') vp.syncStickyAtcPrice(vp.variant);
      } catch (e) {
        /* picker not fully initialised yet — safe to skip */
      }
    });
  }

  var pending = new Set();
  var scheduled = false;

  function flush() {
    scheduled = false;
    var anchors = Array.from(pending);
    pending.clear();
    var pdpSwapped = false;
    anchors.forEach(function (anchor) {
      if (anchorChanged(anchor) && anchor.closest('#pdp-price-container')) pdpSwapped = true;
      recomputeForAnchor(anchor);
    });
    if (pdpSwapped) resyncPdpChrome();
  }

  function schedule(anchor) {
    registerAnchor(anchor);
    pending.add(anchor);
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  }

  function onMutations(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      var target = m.target.nodeType === 3 ? m.target.parentElement : m.target;
      if (target && target.closest) {
        var anchor = target.closest(ANCHOR_SELECTOR);
        if (anchor) schedule(anchor);
      }
      // New subtrees (variant-change re-render, quick view, AJAX pagination,
      // Algolia results) may contain fresh anchors already swapped or about
      // to be swapped — register + check them.
      if (m.addedNodes && m.addedNodes.length) {
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(ANCHOR_SELECTOR)) schedule(node);
          if (node.querySelectorAll) {
            node.querySelectorAll(ANCHOR_SELECTOR).forEach(schedule);
          }
        }
      }
    }
  }

  function init() {
    scanAll(document);
    new MutationObserver(onMutations).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
